import { TabType } from 'components/Tabs';
import Workflow, { WorkflowFacetOptions } from 'models/Workflow';

// Which coverage each tab shows, and in which order (OLIO-2794).
//
// Three FIXED tabs — Primary, Secondary, History — rather than one per coverage. The vocabulary is
// fixed at two coverages, so the strip is stable: a referral with one coverage still shows Secondary
// (holding its empty state) rather than the strip changing shape per referral.
//
// The words are fixed here too. A coverage carries its own `label` from the API, but the tab text is
// vocabulary, not data — a coverage labelled by its payer would put "Anthem" where "Primary" belongs.
// The label is still what the coverage's own heading uses elsewhere.
//
// Which coverage sits in which tab is its `position`, the rank the engine assigned from its own
// primary/secondary marking — never its place in the list (OLIO-3141).

export const COVERAGE_TAB_LABELS = ['Primary', 'Secondary'] as const;

// The run log, spanning coverages — its own tab rather than a section inside each one, per the mocks.
export const HISTORY_TAB = 'history';

// The step's one note thread, spanning coverages and re-runs. Not minted by `coverageTabs` below:
// that strip describes coverages, and the slideout appends this tab only when there is a
// verification to hang the thread on.
export const NOTES_TAB = 'notes';

// Live coverages, in the order the API says they belong in — `position`, not the order they happen to
// arrive in. A coverage added later can still be the primary one (OLIO-2794 AC on ordering), and an
// archived coverage is a person's deliberate removal, so it isn't a tab.
//
// Delegates to the model rather than repeating the filter, so the tab strip and the Edit
// Demographics slots cannot disagree about which rank a coverage sits at. They did: `coverageFacets`
// reports a placeholder at the first free declared rank (OLIO-3386) and this read did not, so the
// form offered Primary while the strip drew the same coverage as "Coverage 3".
export function coveragesOf(workflow: Workflow): WorkflowFacetOptions[] {
  return workflow.coverageFacets;
}

// The tab strip for a referral. Empty when the referral has no coverages at all — a referral pinned
// to a configuration from before coverages existed has exactly one verification and nothing to switch
// between, so it keeps today's single view. Offering it a Secondary tab would be worse than
// pointless: its "+ Add Secondary Insurance" posts a coverage its configuration doesn't declare, and
// the API rejects that every time.
export function coverageTabs(workflow: Workflow): TabType[] {
  const coverages = coveragesOf(workflow);
  if (coverages.length === 0) return [];

  // Always at least the two the vocabulary names, so a referral holding only a primary still shows
  // Secondary with its empty state and its add button.
  //
  // But never FEWER tabs than there are coverages. Nothing caps how many a referral can have:
  // Workflow::MintFacets mints one per entry in the extracted list and only enforces a floor, and
  // re-extracting a packet whose payer name was corrected mints an extra coverage by design (its
  // identity changed), which stays live until a person archives it. With a fixed pair of tabs, that
  // third coverage's checklist row would render its body under a strip with no tab to highlight —
  // showing one coverage's results while the strip points at another.
  //
  // A slot is a RANK, not a place in this list (OLIO-3141). The engine ranks a coverage by its own
  // primary/secondary marking, so a packet naming only a secondary leaves rank 0 empty. Counting
  // list places instead put that secondary in the Primary tab and offered "add" on the Secondary
  // one — the referral's only insurance shown as its primary, under a heading that said so.
  //
  // The coverage count is a floor too, so the strip can never have fewer slots than there are
  // coverages. Ranks come from the API and are unique in every payload it produces, but the model
  // defaults a missing one to 0 — and with ranks alone, two coverages sharing one would have left
  // the second with no tab at all, which the old dense index could not do.
  const highestRank = Math.max(...coverages.map((coverage) => coverage.position));
  const slots = Math.max(highestRank + 1, coverages.length, COVERAGE_TAB_LABELS.length);

  return [
    ...Array.from({ length: slots }, (_, index) => ({
      label: coverageTabLabel(index),
      value: String(index),
      'data-cy': `coverage-tab-${index}`,
    })),
    // Last, and spanning coverages rather than sitting inside each one (the mocks).
    { label: 'History', value: HISTORY_TAB, 'data-cy': 'coverage-tab-history' },
  ];
}

// What a coverage is called on screen. Positional beyond the two the vocabulary names, because a
// third coverage's own label would put a payer name where Primary and Secondary sit — UX hasn't
// settled what a third should be called (an open question on OLIO-2794).
export function coverageTabLabel(index: number): string {
  return COVERAGE_TAB_LABELS[index] ?? `Coverage ${index + 1}`;
}

// Which tab a click on a step row should open: the coverage that row belongs to. Falls back to the
// first tab for a row with no coverage.
export function tabForFacet(workflow: Workflow, facetId: string | null): string {
  const facet = coveragesOf(workflow).find((coverage) => coverage.id === facetId);

  return facet ? String(facet.position) : '0';
}

// The coverage a coverage tab is showing, or null when the tab is a slot no coverage fills yet — a
// Secondary tab on a referral that only has a primary. That null is the empty state's cue, and is
// deliberately NOT the same as the null passed for a referral with no coverages at all.
export function coverageForTab(workflow: Workflow, tab: string): WorkflowFacetOptions | null {
  return coveragesOf(workflow).find((coverage) => coverage.position === Number(tab)) ?? null;
}
